import React from "react";
import dayjs, { Dayjs } from "dayjs";
import WorkDay from "../../Types/WorkDay";
import { CountedState } from "../../Types/Enums/CountedState";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import WorkDayRepository from "../../Repositories/WorkDayRepository";
import Enumerable from "linq";
import WorkDayService from "../../Services/WorkDayService";

interface Props {
    workDays: WorkDay[];
    onDaySelected: (day: Dayjs) => void;
    onMonthChanged: (month: Dayjs) => void;
    onWorkDayMoved: (from: Dayjs, to: Dayjs) => void;
}

interface State {
    monthStart: Dayjs;
    selectedDate: Dayjs | null;
}

const ACCENT = "#c79656";
const COLOR_COUNTED = "#4f8f6a";
const COLOR_PARTIAL = "#4b7bbf";
const COLOR_OPEN = ACCENT;
const COLOR_CLOSED = "#9ca3af";
const COLOR_NONE = "#e5e7eb";

const MIME = "application/x-workday-date";

export default class CustomDateOverview extends React.Component<Props, State> {
    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: Props) {
        super(props);
        this.selectDay = this.selectDay.bind(this);
        this.moveWorkDay = this.moveWorkDay.bind(this);
        this.state = {
            monthStart: dayjs().startOf("month"),
            selectedDate: dayjs(),
        };
    }

    private toISO(d: Dayjs) {
        return d.format("YYYY-MM-DD");
    }

    private mondayIndex(jsDay: number) {
        return (jsDay + 6) % 7;
    }

    private prevMonth = () => {
        this.setState(
            ({ monthStart }) => ({ monthStart: monthStart.subtract(1, "month").startOf("month") }),
            () => this.props.onMonthChanged(this.state.monthStart)
        );
    };

    private nextMonth = () => {
        this.setState(
            ({ monthStart }) => ({ monthStart: monthStart.add(1, "month").startOf("month") }),
            () => this.props.onMonthChanged(this.state.monthStart)
        );
    };

    private getWorkDayMap(): Record<string, WorkDay> {
        const map: Record<string, WorkDay> = {};
        for (const d of this.props.workDays) map[this.toISO(dayjs(d.Date))] = d;
        return map;
    }

    private hasData(wd?: WorkDay) {
        return WorkDayService.hasWorkDayData(wd);
    }

    private selectDay(day: Dayjs) {
        this.setState({ selectedDate: day });
        this.props.onDaySelected(day);
        if (day.isAfter(this.state.monthStart.endOf("month"))) this.nextMonth();
        if (day.isBefore(this.state.monthStart)) this.prevMonth();
    }

    private onDragStart = (e: React.DragEvent, from: Dayjs, wd?: WorkDay) => {
        if (!this.hasData(wd) || wd?.CountedState === CountedState.Counted) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(MIME, this.toISO(from));
    };

    private onDragOver = (e: React.DragEvent) => {
        if (Array.from(e.dataTransfer.types).includes(MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
        }
    };

    private onDrop = (e: React.DragEvent, target: Dayjs) => {
        const fromIso = e.dataTransfer.getData(MIME);
        if (!fromIso) return;
        const from = dayjs(fromIso, "YYYY-MM-DD", true);
        if (!from.isValid() || from.isSame(target, "day")) return;
        this.moveWorkDay(from, target);
    };

    private async moveWorkDay(from: Dayjs, to: Dayjs) {
        const workDay = Enumerable.from(this.props.workDays).single(x => dayjs(x.Date).isSame(from));
        const targetDate = to.format("YYYY-MM-DD");
        const workPeriods = (workDay.WorkPeriods ?? []).map((period) => {
            const moved = WorkDayService.cloneWorkPeriod(period);
            moved.Date = targetDate;
            return moved;
        });
        if (workDay.CountedState === CountedState.Counted) return;
        const result = await WorkDayRepository.updateWorkDay(
            this.context.locationId,
            new WorkDay(workDay.Id, targetDate, workDay.TipPin, workDay.TipCash, workDay.IsClosed, workDay.CountedState, workDay.CountedBy, workDay.Workers, workPeriods)
        );
        if (result.isFailed()) return;
        this.props.onWorkDayMoved(from, to);
    }

    public render() {
        const { monthStart, selectedDate } = this.state;
        const first = monthStart.startOf("month");
        const startCell = first.subtract(this.mondayIndex(first.day()), "day");
        const cells: Dayjs[] = Array.from({ length: 42 }, (_, i) => startCell.add(i, "day"));
        const dayMap = this.getWorkDayMap();

        return (
            <div style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                boxSizing: "border-box",
                overflow: "hidden",
            }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0f172a" }}>
                            <div style={{ width: 4, height: 18, borderRadius: 4, background: ACCENT }} />
                            <span style={{ fontSize: 15, textTransform: "capitalize" }}>
                                {first.format("MMMM YYYY")}
                            </span>
                        </div>
                        <div style={{ display: "flex", gap: 10, fontSize: 10, color: "rgba(15,23,42,0.55)", userSelect: "none", paddingLeft: 12 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ width: 5, height: 5, borderRadius: 99, background: COLOR_COUNTED, display: "inline-block" }} />
                                Counted
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ width: 5, height: 5, borderRadius: 99, background: COLOR_PARTIAL, display: "inline-block" }} />
                                Partial
                            </span>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 6 }}>
                        {[{ label: "◀", fn: this.prevMonth, aria: "Previous month" }, { label: "▶", fn: this.nextMonth, aria: "Next month" }].map(btn => (
                            <button
                                key={btn.aria}
                                onClick={btn.fn}
                                aria-label={btn.aria}
                                style={{
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    background: "white",
                                    borderRadius: 8,
                                    width: 30,
                                    height: 30,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    color: "rgba(0,0,0,0.55)",
                                }}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Weekday labels */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 4,
                    flexShrink: 0,
                }}>
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map(w => (
                        <div key={w} style={{ textAlign: "center", fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.45)", userSelect: "none" }}>
                            {w}
                        </div>
                    ))}
                </div>

                {/* Day grid — fills remaining space */}
                <div style={{
                    flex: 1,
                    minHeight: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gridTemplateRows: "repeat(6, 1fr)",
                    gap: 4,
                    userSelect: "none",
                }}>
                    {cells.map((d, idx) => {
                        const inMonth = d.isSame(first, "month");
                        const wd = dayMap[this.toISO(d)];
                        const counted = wd?.CountedState ?? CountedState.NotCounted;
                        const hasData = this.hasData(wd);
                        const isCounted = counted === CountedState.Counted;
                        const isPartial = counted === CountedState.PartiallyCounted;
                        const isSelected = !!selectedDate && selectedDate.isSame(d, "day");

                        const borderColor = isSelected ? ACCENT : isCounted ? COLOR_COUNTED : isPartial ? COLOR_PARTIAL : "rgba(0,0,0,0.08)";
                        const bg = isSelected
                            ? "rgba(199,150,86,0.07)"
                            : isCounted
                                ? "rgba(79,143,106,0.09)"
                                : isPartial
                                    ? "rgba(75,123,191,0.09)"
                                    : "white";

                        return (
                            <button
                                key={idx}
                                onClick={() => this.selectDay(d)}
                                draggable={hasData && !isCounted}
                                onDragStart={e => this.onDragStart(e, d, wd)}
                                onDragOver={this.onDragOver}
                                onDrop={e => this.onDrop(e, d)}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: `1px solid ${borderColor}`,
                                    background: bg,
                                    borderRadius: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    padding: 0,
                                    gap: 3,
                                    boxShadow: (isSelected || isCounted || isPartial)
                                        ? `0 4px 12px -6px ${borderColor}88`
                                        : "none",
                                    transition: "box-shadow 120ms ease",
                                }}
                            >
                                <span style={{
                                    fontSize: "clamp(9px, 1.4cqw, 13px)",
                                    fontWeight: 700,
                                    color: inMonth ? "#0f172a" : "rgba(15,23,42,0.3)",
                                    lineHeight: 1,
                                }}>
                                    {d.date()}
                                </span>
                                <span style={{
                                    width: "40%",
                                    height: 3,
                                    borderRadius: 99,
                                    background: hasData ? (wd?.IsClosed ? COLOR_CLOSED : COLOR_OPEN) : COLOR_NONE,
                                    opacity: inMonth ? 0.7 : 0.3,
                                    display: "block",
                                }} />
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
}
